/**
 * Shared handling for `processEvent` (integration event) errors.
 *
 * Extracted from EditIntegrationItemFilesDialog so both CompanyCam pickers use
 * the same logic: the new AddFileDialog previously had none at all, so a failed
 * `get-files` call rendered as an empty photo grid — indistinguishable from a
 * project with no photos (#21756).
 */

const PROVIDER_DISPLAY_NAMES: Record<string, string> = { companycam: 'CompanyCam' }

/**
 * processEvent surfaces errors on more than one path depending on how the
 * integration failed:
 * - thrown error (e.g. the CompanyCam HTTP call): a string at the top-level `processEvent.error`
 * - structured resolver branch (no_owner, process_event_error, no_active_providers):
 *   `{ name, message }` at `processEvent.error`
 * - handler-returned error (e.g. our `not_found` token): at `processEvent.result.error`
 */
export type EventError = string | { name?: string; message?: string }

export function extractEventError(data: any): EventError | null {
  return data?.processEvent?.error ?? data?.processEvent?.result?.error ?? null
}

/**
 * Message for an error caught around a `processEvent` call. Catch bindings are
 * `unknown`, so reach for `message` through a narrow rather than an `any` cast.
 * The result is a plain string, which is itself a valid `EventError`.
 */
export function getCaughtErrorMessage(err: unknown, fallback = 'Unknown error'): string {
  if (typeof err === 'string' && err) {
    return err
  }
  const message = (err as { message?: unknown })?.message
  return typeof message === 'string' && message ? message : fallback
}

export function getEventErrorMessage(error: EventError | null): string | null {
  if (!error) {
    return null
  }
  if (typeof error === 'string') {
    return error
  }
  if (typeof error.message === 'string') {
    return error.message
  }
  return null
}

export function getProviderDisplayName(
  provider: string | undefined,
  __: (key: string, ...args: any[]) => string
): string {
  if (!provider) {
    return __('Integration')
  }
  return PROVIDER_DISPLAY_NAMES[provider] ?? provider.charAt(0).toUpperCase() + provider.slice(1)
}

/**
 * True when the provider told us the thing we asked for is gone — e.g. a
 * CompanyCam project the picker remembered in localStorage but the user has
 * since deleted. Callers use this to forget the stale id instead of leaving the
 * user staring at a permanently empty grid.
 *
 * Matches both the stable `not_found` token our handlers return and the raw
 * "Not found" message CompanyCam 404s produce when a thrown error is flattened
 * (older API builds, and any other event that doesn't catch the 404).
 */
export function isNotFoundEventError(error: EventError | null): boolean {
  const message = getEventErrorMessage(error)
  if (!message) {
    return false
  }
  return /^not[\s_-]?found$/i.test(message.trim())
}
